import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import WhoWillBeLearningStep from "@/components/onboarding/WhoWillBeLearningStep";
import LearnerIdentityForm from "@/components/learners/LearnerIdentityForm";
import StartLearningButton from "@/components/learners/StartLearningButton";
import type { LearnerChoice } from "@/lib/learners/whoIsLearning";
import { trackSeatEvent } from "@/lib/telemetry/seatTelemetry";

type Props = {
  userId: string;
  householdId: string;
  maxLearners: number;
  profile: { display_name: string | null; username: string | null };
  onComplete: () => void;
};

type Step = "who" | "identity" | "done";

const includesSelf = (c: LearnerChoice) => c === "self" || c === "both";
const includesChild = (c: LearnerChoice) => c !== "self";

/** Cutover-ON onboarding: owner is not forced to add a child; self learners get a seat + identity. */
export default function SeatOnboardingFlow({ userId, householdId, maxLearners, profile, onComplete }: Props) {
  const [step, setStep] = useState<Step>("who");
  const [choice, setChoice] = useState<LearnerChoice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const finish = async (c: LearnerChoice) => {
    const { error: saveError } = await supabase.from("profiles").upsert(
      { id: userId, learner_intent: c, onboarding_completed: true, onboarding_completed_at: new Date().toISOString() },
      { onConflict: "id" },
    );
    if (saveError) {
      setError("We couldn't finish setting up your account. Please try again.");
      return;
    }
    trackSeatEvent("onboarding_completed", { learner_intent: c });
    setStep("done");
  };

  const handleChoice = async (c: LearnerChoice) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setChoice(c);
    trackSeatEvent("onboarding_learner_intent_selected", { learner_intent: c, seat_max: maxLearners });
    try {
      if (includesSelf(c)) {
        const { error: rpcError } = await supabase.rpc("assign_learner_seat", { p_household_id: householdId, p_learner_kind: "self" });
        if (rpcError && !/already|duplicate|unique/i.test(rpcError.message)) {
          setError(/FULL|CAPACITY_EXCEEDED|CAPACITY/i.test(rpcError.message)
            ? "All learner seats are in use. Free a seat or choose parent access only."
            : "We couldn't set up your learner seat. Please try again.");
          return;
        }
        if (!profile.display_name?.trim() || !profile.username?.trim()) {
          await supabase.from("profiles").upsert({ id: userId, learner_intent: c }, { onConflict: "id" });
          setStep("identity");
          return;
        }
      }
      await finish(c);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] overflow-y-auto bg-background/95 backdrop-blur-xl">
      <main className="mx-auto max-w-md px-4 py-10" aria-live="polite">
        {step === "who" && (
          <>
            <WhoWillBeLearningStep maxLearners={maxLearners} onSubmit={handleChoice} />
            {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
          </>
        )}
        {step === "identity" && choice && (
          <section aria-labelledby="identity-heading" className="space-y-4">
            <h2 id="identity-heading" className="font-heading text-xl font-bold text-foreground">Set up your learner profile</h2>
            <LearnerIdentityForm
              userId={userId}
              initialDisplayName={profile.display_name}
              initialUsername={profile.username}
              onSaved={() => void finish(choice)}
            />
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
          </section>
        )}
        {step === "done" && choice && (
          <section aria-labelledby="done-heading" className="space-y-4 text-center">
            <h2 id="done-heading" className="font-heading text-xl font-bold text-foreground">You're all set</h2>
            {includesSelf(choice) && <StartLearningButton className="w-full" onBeforeNavigate={onComplete} />}
            {includesChild(choice) && (
              <Link
                to="/parent-dashboard?tab=family"
                onClick={onComplete}
                className="inline-flex w-full items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted"
              >
                Set up your child
              </Link>
            )}
            {includesChild(choice) && (
              <p className="text-xs text-muted-foreground">You can add your child now or later. Your seat stays saved until then.</p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
